import { useEffect, useRef, useState } from 'react';
import CheckboxControl from './CheckboxControl';
import AddressSearch from './AddressSearch.jsx';

export default function Sidebar({
  stores,
  totalCount,
  query,
  onQueryChange,
  categories,
  activeCategories,
  onToggleCategory,
  selectedId,
  onSelect,
  onLocateMe,
  onPlaceSelect,
  t,
}) {
  const listRef = useRef(null);
  const [openIds, setOpenIds] = useState(new Set());

  const days = t('days');

  const toggleDetails = (e, id) => {
    e.stopPropagation();
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (!selectedId || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-store-id="${selectedId}"]`);
    if (window.innerWidth >= 1024) {
      if (el) {
        const container = listRef.current;
        const offset = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
        container.scrollTo({ top: container.scrollTop + offset, behavior: 'smooth' });
      }
    }
  }, [selectedId]);

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <h1 className="Headline-3">{t('sidebar.heading')}</h1>
        <p className="subtitle text-black Body-4-b">{t('sidebar.subtitle')}</p>

        <AddressSearch
          query={query}
          onQueryChange={onQueryChange}
          onPlaceSelect={onPlaceSelect}
          onLocateMe={onLocateMe}
        />

        <div className="filters">
          {categories.map((cat) => (
            <CheckboxControl
              key={cat}
              id={`filter-${cat}`}
              name={`filter-${cat}`}
              label={cat}
              checked={activeCategories.includes(cat)}
              onChange={() => onToggleCategory(cat)}
            />
          ))}
        </div>
      </div>

      <div className="store-list">
        <p className="count Body-4-d">{t('sidebar.storeCount', { count: totalCount })}</p>
        <div className="store-list-items" ref={listRef}>
          {stores.length === 0 && <div className="empty">{t('sidebar.noResults')}</div>}
          {stores.map((store) => {
            const cat = store.shop_types.find((t) => categories.includes(t));
            const selected = store.id === selectedId;
            const detailsOpen = openIds.has(store.id);
            return (
              <div
                key={store.id}
                data-store-id={store.id}
                className={`store-card ${selected ? 'selected' : ''}`}
                onClick={() => {
                  onSelect(store.id);
                  if (window.innerWidth < 1024) {
                    const mapEl = document.querySelector('.map-wrap');
                    if (mapEl) mapEl.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                <div className="card-top">
                  <span className="cat-label Body-4-e">{cat}</span>
                </div>
                <div className="card-content">
                  <div className="store-name-wrapper">
                    <h3 className="store-name Headline-4">{store.store_name.toLowerCase()}</h3>
                    <button className="details-btn Body-4-e" onClick={(e) => toggleDetails(e, store.id)}>
                      {detailsOpen ? t('sidebar.detailsHide') : t('sidebar.detailsShow')}
                    </button>
                  </div>
                </div>

                <p className="store-addr Body-4-b">
                  {store.address.map((line, i) => (
                    <span key={i}>
                      {i > 0 && <br />}
                      {line.toLowerCase()}
                    </span>
                  ))}
                </p>
                <div className="store-phones">
                  {store.phone_number.map((phone, i) => (
                    <a
                      key={i}
                      className="store-phone Body-4-b"
                      href={`tel:${phone.replace(/\s/g, '')}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {phone}
                    </a>
                  ))}
                </div>
                {store.termporary_closed === true && (
                  <span className="store-closed Body-4-b">{t('sidebar.temporarilyClosed')}</span>
                )}
                {detailsOpen && store.opening_hours?.length > 0 && (
                  <div className="store-hours">
                    {store.opening_hours.map((hours, i) => (
                      <div key={i} className="hours-row">
                        <span className="hours-day Body-4-b">{Array.isArray(days) ? days[i] : ''}</span>
                        <span className="hours-time Body-4-b">{hours}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="card-actions">
                  <a
                    className="action-link Body-3-b"
                    href={`https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('sidebar.getDirections')}
                  </a>
                  {store.trigger_popup_form && (
                    <button
                      type="button"
                      className={`action-link Body-3-b ${store.trigger_popup_form}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('sidebar.bookAppointment')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
